import { AfterViewInit, Component, DestroyRef, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemEvent, SystemEventDismissalService, SystemEventsHubService, SystemEventsRepositoryService, Utilities } from 'reg-hub-common';
import {
    trigger,
    style,
    transition,
    animate,
} from '@angular/animations';
import { HttpParams } from '@angular/common/http';
import { interval } from 'rxjs';

@Component({
    selector: 'app-system-event-notification',
    templateUrl: './system-event-notification.component.html',
    styleUrls: ['./system-event-notification.component.css'],
    animations: [
        trigger('expandHeight', [
            transition(':enter', [
                style({ height: '0', opacity: 0 }),
                animate('250ms ease-in-out', style({ height: '*', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('250ms ease-in-out', style({ height: '0', opacity: 0 })),
            ]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SystemEventNotificationComponent implements AfterViewInit, OnDestroy {
    protected _events: SystemEvent[] = [];
    protected _eventMessage: string | null = null;

    private destroyRef = inject(DestroyRef);

    constructor(
        protected repo: SystemEventsRepositoryService,
        protected hub: SystemEventsHubService,
        protected dismissals: SystemEventDismissalService) { }

    ngAfterViewInit(): void {
        this.repo.getFirstPageSubscribable(new HttpParams()
            .append('isDisplayed', true)
            .append('isCustomerVisible', true)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(events => {
                this.setEvents(events);
            });

        this.hub.connect();
        this.hub.events$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(events => {
            // The hub broadcasts every current event, including internal ones, so match the initial fetch's filter.
            this.setEvents((events ?? []).filter(event => event.isCustomerVisible));
        });

        //checking in memory events periodically for expired ones (the tick also re-renders the notice/warning style)
        interval(10000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this._events.length > 0) {
                const stillOpen = this._events.filter(event => Utilities.isSystemEventDisplayed(event));

                // Re-run through setEvents so dismissals are also honoured.
                this.setEvents(stillOpen);
            }
        })
    }

    ngOnDestroy(): void {
        this.hub.disconnect();
    }

    // Dismiss just for this session — the event returns on reload or the next push.
    dismissForNow(index: number) {
        this._events.splice(index, 1);
    }

    // Dismiss for a day — persisted in localStorage so it survives reloads and pushes.
    dismissForOneDay(event: SystemEvent) {
        this.dismissals.dismiss(event.id);
        // Re-filter the current list so the dismissed event drops out immediately.
        this.setEvents(this._events);
    }

    // Blue notice before a scheduled outage starts, red warning once the service is down.
    protected isNotice(event: SystemEvent): boolean {
        return Utilities.isSystemEventNotice(event);
    }

    private setEvents(events: SystemEvent[]) {
        this._events = this.dismissals.filterDismissed(events ?? []);
        this._eventMessage = this._events.length > 0 ? this._events[0].message : null;
    }
}